/**
 * useDatabaseManagement Hook - Database Management
 * No synthetic data: all values come from API or stay empty with an error.
 */

import { useState, useEffect, useCallback } from "react";
import { API_ENDPOINTS, apiFetch, apiFetchJson } from "@/lib/api";

export interface DatabaseHealth {
    service: "postgresql" | "redis";
    status: "healthy" | "down";
    response_time_ms: number;
    last_check: string;
    error?: string;
    connections?: {
        active: number;
        max: number;
        idle: number;
    };
    memory?: {
        used_mb: number;
        max_mb?: number | null;
    };
}

export interface DatabaseMetrics {
    postgresql: {
        uptime_seconds: number;
        connections: {
            active: number;
            max: number;
            idle: number;
        };
        queries: {
            total: number;
            slow_queries: number;
            avg_duration: number;
        };
        size: {
            database_size_gb: number;
            table_count: number;
            index_count: number;
        };
        performance: {
            cache_hit_ratio: number;
            commits_per_sec: number;
            rollbacks_per_sec: number;
        };
    };
    redis: {
        uptime_seconds: number;
        memory: {
            used_mb: number;
            max_mb: number;
            fragmentation_ratio: number;
        };
        keys: {
            total: number;
            expired: number;
            evicted: number;
        };
        performance: {
            ops_per_sec: number;
            hit_ratio: number;
            keyspace_hits: number;
            keyspace_misses: number;
        };
        clients: {
            connected: number;
            blocked: number;
        };
    };
}

export interface DatabaseBackup {
    id: string;
    type: "postgresql" | "redis";
    filename: string;
    size_mb: number;
    created_at: string;
    status: "completed" | "running" | "failed";
    duration_seconds?: number;
}

export interface QueryResult {
    columns: string[];
    rows: any[];
    execution_time: number;
    affected_rows?: number;
}

export function useDatabaseManagement() {
    const [health, setHealth] = useState<DatabaseHealth[]>([]);
    const [metrics, setMetrics] = useState<DatabaseMetrics | null>(null);
    const [backups, setBackups] = useState<DatabaseBackup[]>([]);
    const [queryResult, setQueryResult] = useState<QueryResult | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const fetchDatabaseHealth = useCallback(async () => {
        const started = Date.now();
        try {
            setError(null);
            const res = await apiFetch(API_ENDPOINTS.DATABASE_HEALTH);
            const rt = Date.now() - started;
            if (!res.ok) {
                setHealth([]);
                setError(
                    `Database health non disponibile (HTTP ${res.status})`,
                );
                return;
            }

            const data = await res.json();
            const pgOk = Boolean(data?.postgresql?.ok);
            const redisOk = Boolean(data?.redis?.ok);

            setHealth([
                {
                    service: "postgresql",
                    status: pgOk ? "healthy" : "down",
                    response_time_ms: rt,
                    last_check: new Date().toISOString(),
                    error: data?.postgresql?.error || undefined,
                    connections: data?.postgresql?.connections || undefined,
                },
                {
                    service: "redis",
                    status: redisOk ? "healthy" : "down",
                    response_time_ms: rt,
                    last_check: new Date().toISOString(),
                    error: data?.redis?.error || undefined,
                    memory: data?.redis?.memory || undefined,
                },
            ]);
        } catch (e) {
            setHealth([]);
            setError(e instanceof Error ? e.message : "Unknown error");
        }
    }, []);

    const fetchDatabaseMetrics = useCallback(async () => {
        try {
            const data = await apiFetchJson<any>(API_ENDPOINTS.DATABASE_METRICS);
            setMetrics(data || null);
        } catch (e) {
            setMetrics(null);
            setError(e instanceof Error ? e.message : "Unknown error");
        }
    }, []);

    const fetchBackups = useCallback(async () => {
        try {
            const data = await apiFetchJson<any>(API_ENDPOINTS.DATABASE_BACKUPS);
            setBackups(data?.backups || []);
        } catch (e) {
            setBackups([]);
            setError(e instanceof Error ? e.message : "Unknown error");
        }
    }, []);

    const refresh = useCallback(async () => {
        setIsLoading(true);
        await Promise.all([
            fetchDatabaseHealth(),
            fetchDatabaseMetrics(),
            fetchBackups(),
        ]);
        setIsLoading(false);
    }, [fetchBackups, fetchDatabaseHealth, fetchDatabaseMetrics]);

    useEffect(() => {
        refresh();
    }, [refresh]);

    return {
        health,
        metrics,
        backups,
        queryResult,
        isLoading,
        error,
        refresh,
        setQueryResult,
    };
}
